import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Modal } from 'zarm';
import { Icon } from '@iyunbao/components';
import Form from 'react-form-validates';

import { getFormId, replaceValidate, clearObj, toTrim } from '../../utils/tools';
import { BaseWidgetProps, ValidateProps, RulesProps, BaseFormProps } from '../../utils/type';

import WarnRules from './warnRules';

const { Item: FormItem } = Form;

export interface UItemProps extends BaseWidgetProps {
  itemType?: string;
  samePath?: boolean;
  dataBind?: string;
  path?: string;
  hasArrow?: boolean;
  linefeed?: boolean | undefined;
  isLabel?: boolean;
  label?: string | React.ReactNode;
  suffix?: string | React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  validate?: ValidateProps;
  description?: string | React.ReactNode;
  warnRules?: any;
  rules?: any;
  initialValue?: any;
  readOnly?: boolean;
  subLabel?: string;
  validateTrigger?: string,
  tips?: any;
  children?: any;
  ref?: any;
  vertical?: boolean;
}

interface DecoratorProps {
  form?: BaseFormProps;
  itemType?: string;
  valueTrim?: string;
  samePath?: boolean;
  initialValue?: any;
  rules?: RulesProps[];
  validate?: ValidateProps;
  dataBind?: string;
  path?: string| string[];
  onChange: (params: any) => {};
  onBlur?: (params: any) => {};
  validateTrigger?: string | string[];
}

export function setDecoratorItem(props: DecoratorProps, children?: PropTypes.ReactElementLike) {
  const {
    form,
    initialValue,
    validate = {},
    dataBind,
    path,
    itemType,
    // form value to trim
    valueTrim,
    samePath,
    onChange,
    onBlur,
    validateTrigger = 'onChange',
  } = props;

  // console.log(222, props);

  try {
    if (form) {
    // samePath用于form有子级内容，路径会出现问题。switchPanel组件
      const names = ((samePath && typeof path === 'string' && path.match(dataBind || '')) ? path : getFormId(dataBind, path));

      if (!names) {
        console.error('Form 组件 name 不能为空');
        return;
      }

      const { rules, ...otherProps } = replaceValidate(validate);
      const { getFieldDecorator } = form;
      const optionProps: any = {};

      // 渲染引擎 _crengine valueTrim
      // 手机输入框自动带入前后空格，特殊处理
      // 组件输入框、邮箱、证件选择
      if (
        valueTrim
      && itemType
      && !!~['input', 'email', 'selectCert'].indexOf(itemType)
      ) {
        optionProps.normalize = (value: any, prevValue: any, all: any) => {
          // 如果是身份证，则身份证大写
          if (itemType === 'selectCert') {
            const keys = Object.keys(all).filter(item => item.includes('certType')) || '';
            keys.forEach(key => {
              const certNoKey = key.replace('certType', 'certNo');
              value = all[key] === 'SFZ' && all[certNoKey] === value ? value.toUpperCase() : value;
            });
          }
        // 离开事件触发
          if (typeof value === 'string' && value === prevValue) {
            return toTrim(value);
          }

          return value;
        };
      }

      const options = clearObj({
        rules,
        initialValue,
        validateTrigger,
        onBlur,
        onChange,
        ...optionProps,
        ...otherProps,
      });

      // console.log(111, names);

      return getFieldDecorator(names, options)(children);
    }
  } catch (error) {
    console.error('setDecoratorItem===', error);
  }

  return children;
}

/**
 * 提示文案
 * @param props
 */
function HelpRules(props: any) {
  const { rules, prefixCls, _crengine = {} } = props;
  const { form, dataBind, path } = _crengine;

  if (!(rules && Array.isArray(rules) && rules.length)) {
    return null;
  }

  // 最多只显示三条报错信息
  const newRules = rules.filter((_v, index) => index < 3);
  const names = `help.${getFormId(dataBind, path)}`;
  const len = rules.length;
  const message = (len ? rules[0] : '');

  return (
    <>
      {
        (len && form) ? form.getFieldDecorator(names, {
          rules: [{ required: true, message }],
          initialValue: (len ? '' : '1'),
        })(
          <input type="hidden" />,
        ) : null
      }
      {
        newRules.map((text: string, key: number) => (
          /* eslint-disable react/no-array-index-key */
          <div className={`${prefixCls}-explain`} key={`item_${key}`}>{String(text)}</div>
        ))
      }
    </>
  );
}

const UItem = (props: UItemProps) => {
  const {
    itemType,
    // 相同路径
    samePath,
    hasArrow,
    linefeed,
    isLabel = true,
    children,
    label,
    subLabel,
    className,
    warnRules,
    description,
    tips,
    style,
    rules,
    validate,
    initialValue,
    readOnly,
    validateTrigger = 'onChange',
    _crengine,
    vertical = false,
    onChange,
    onBlur,
  } = props;
  const prefixCls = 'crm-form-item';

  const { form, path, dataBind, valueTrim } = _crengine || {};

  const validatorProps = clearObj({
    path,
    rules,
    form,
    itemType,
    valueTrim,
    samePath,
    validate,
    dataBind,
    initialValue,
    validateTrigger,
    onChange,
    onBlur,
  });

  const cls = classnames(prefixCls, {
    [`${prefixCls}-vertical`]: vertical,
    [`${prefixCls}-linefeed`]: linefeed,
    [`${prefixCls}-${itemType}`]: !!itemType,
    [`${className}-item`]: !!className,
  });

  const formItemProps: any = {
    className: cls,
    hasArrow,
    style,
  };

  // @TODO 待修正
  if (!label) {
    const tempCls = classnames(`${prefixCls} ${prefixCls}-control`, {
      [`${prefixCls}-linefeed`]: linefeed,
      [`${prefixCls}-control-${itemType}`]: !!itemType,
    });

    return (
      <>
        <FormItem
          className={tempCls}
          hasArrow={hasArrow}
          description={description}
          {...formItemProps}
        >
          {setDecoratorItem(validatorProps, children)}
        </FormItem>
        <HelpRules rules={rules} prefixCls={prefixCls} _crengine={_crengine} />
        <WarnRules prefixCls={`${prefixCls}-warn-rules`} dataSource={warnRules} />
      </>
    );
  }

  if (isLabel && typeof label !== 'undefined') {
    const labelRender = [<span key="label">{label}</span>];

    if (tips) {
      labelRender.push(
        <Icon
          key="icon"
          type="wenhao"
          onClick={() => Modal.alert({
            content: tips,
          })}
        />,
      );
    }

    // 子级描写 && 可输入状态
    if (subLabel && !readOnly) {
      labelRender.push(<span className="optional" key="optional">{subLabel}</span>);
    }

    formItemProps.label = labelRender;
  }

  return (
    <>
      <FormItem
        description={description}
        {...formItemProps}
      >
        {setDecoratorItem(validatorProps, children)}
      </FormItem>
      <HelpRules rules={rules} prefixCls={prefixCls} _crengine={_crengine} />
      <WarnRules prefixCls={`${prefixCls}-warn-rules`} dataSource={warnRules} />
    </>
  );
};

export default UItem;
